import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import {
  musicProductDigitalInitialValues,
  musicProductDigitalTabs,
} from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiCopy, PiPencilSimpleLine } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useDeleteMusicProductDigital,
  useGetMusicProductDigital,
} from 'services/music-product-digital';
import { DIGITAL_PRODUCT_TYPE } from 'services/music-product-digital/types/enums';
import { usePostMusicProductPhysicalFromDigital } from 'services/music-product-physical';
import { STATUS } from 'services/types';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastTabPathname } from 'utils/navbar/getParentPathname';
import {
  onErrorShowAllErrorsToast,
  onErrorShowToast,
} from 'utils/service/errorHandler';

const MusicProductDigital = () => {
  const musicProductDigitalId = useParamId('music-product-digital');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const { data: musicProductDigital, isLoading } = useGetMusicProductDigital(
    musicProductDigitalId
  );

  const removeMusicProductDigital = useDeleteMusicProductDigital(
    musicProductDigitalId
  );

  const musicProductDigitalIndividualInitialValues = musicProductDigital
    ? middleware.musicProductDigital.queryToForm(musicProductDigital)
    : musicProductDigitalInitialValues;

  const goToEditMusicProductDigital = () => {
    musicProductDigital &&
      navigate(
        `/music-product-digital/edit/${musicProductDigitalId}/${getLastTabPathname(
          pathname
        )}`,
        {
          replace: true,
        }
      );
  };

  const onRemoveMusicProductDigital = async (actions: PopupActions) => {
    await removeMusicProductDigital.mutateAsync(undefined, {
      onError: error => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => navigate('/music-product-digital'),
    });
  };

  const createPhysical = usePostMusicProductPhysicalFromDigital();
  const onCreateMusicProductPhysical = async () =>
    await createPhysical.mutateAsync(musicProductDigitalId, {
      onSuccess: physicalId =>
        navigate(`/music-product-physical/${physicalId}/tracks`),
      onError: error => onErrorShowAllErrorsToast(error),
    });

  return (
    <Form.MusicProductDigital
      i18nextPrefix='music-product-digital'
      onSubmit={() => {}}
      initialValues={musicProductDigitalIndividualInitialValues}
      tittle={getMainTitle(musicProductDigital?.mainTitle)}
      disableTabs={
        musicProductDigital?.releaseStatus === STATUS.draft
          ? [
              musicProductDigitalTabs.delivery,
              musicProductDigitalTabs.smartlink,
              musicProductDigitalTabs.assetIds,
            ]
          : []
      }
      backPath='music-product-digital'
      status={{ displayMode: true, isLoading }}
      topbarContent={
        <div className='flex'>
          {hasPermission('PhysicalMusicProductPermissionsLIST**', 1) &&
            musicProductDigital?.productType === DIGITAL_PRODUCT_TYPE.audio && (
              <Button
                isClear
                onlyIcon
                onClick={onCreateMusicProductPhysical}
                isLoading={createPhysical.isPending}>
                <Button.Icon icon={<PiCopy />} />
                <Button.Text
                  text={t('music-product-digital.create-physical')}
                />
              </Button>
            )}
          {hasPermission('DigitalMusicProductPermissionsLIST**', 2) && (
            <Button isClear onlyIcon onClick={goToEditMusicProductDigital}>
              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('DigitalMusicProductPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              name={musicProductDigital?.mainTitle.title}
              onRemove={onRemoveMusicProductDigital}
            />
          )}
        </div>
      }
    />
  );
};

export default MusicProductDigital;
